import { useState, useEffect, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import Select from "react-select";
import api from "api/api";
import PropTypes from "prop-types"; // Add PropTypes import

// Material UI Components
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import { CircularProgress } from "@mui/material";

// Icons
import ClearIcon from "@mui/icons-material/Clear";

// Components
import CustomOption from "./CustomOption";
import Loader from "../../common/Loader";

// Styles
import ReactSelectStyles from "../../../styles/ReactSelectStyles";

// Store
import { showNotification } from "store/features/notificationSlice";

function AuditingVariablesDocumentDialog({ open = false, setOpen }) {
    const dispatch = useDispatch();
    const productCategoryData = useSelector((state) => state.settingsStore.productCategory);
    const productCategoryId = productCategoryData?.value || null;

    // State for cascading dropdowns
    const [studyTypeOptions, setStudyTypeOptions] = useState([]);
    const [studyTypeMethodOptions, setStudyTypeMethodOptions] = useState([]);
    const [backendVariableOptions, setBackendVariableOptions] = useState([]);
    const [selectedStudyType, setSelectedStudyType] = useState([]);
    const [selectedStudyTypeMethod, setSelectedStudyTypeMethod] = useState([]);
    const [selectedBackendVariable, setSelectedBackendVariable] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [loading, setLoading] = useState({
        studyType: false,
        studyTypeMethod: false,
        backendVariable: false,
    });

    // Handle error function
    const handleError = useCallback(
        (type, error) => {
            dispatch(showNotification({ severity: type, data: error }));
        },
        [dispatch]
    );

    // Helper functions
    const buildUrl = useCallback((baseUrl, productCategoryId, additionalParams = {}) => {
        const params = new URLSearchParams();

        if (productCategoryId) {
            params.append("product_category", productCategoryId);
        }

        Object.keys(additionalParams).forEach((key) => {
            if (additionalParams[key]) {
                params.append(key, additionalParams[key]);
            }
        });

        return `${baseUrl}?${params.toString()}`;
    }, []);

    const transformData = useCallback((data, key) => {
        if (!Array.isArray(data)) return [];
        if (key !== "backend_variable") {
            return data
                .map((item) => ({
                    label: item.title || "Unknown",
                    value: item.id || "unknown",
                }))
                .filter((item) => item.label !== "Unknown");
        }
        return data
            .map((item) => ({
                label: item || "Unknown",
                value: item || "unknown",
            }))
            .filter((item) => item.label !== "Unknown");
    }, []);

    // API Functions
    const fetchStudyTypes = useCallback(async () => {
        setLoading((prev) => ({ ...prev, studyType: true }));
        try {
            const baseUrl = process.env.REACT_APP_SERVER_PATH + `/study-types/all/`;
            const url = buildUrl(baseUrl, productCategoryId);

            await api
                .get(url)
                .then((res) => {
                    const data = Array.isArray(res.data) ? res.data : [];
                    const options = transformData(data, "study_type");
                    setStudyTypeOptions(options);
                    setLoading((prev) => ({ ...prev, studyType: false }));
                })
                .catch((error) => {
                    console.error("Error fetching study types:", error);
                    handleError("error", "Failed to fetch study types");
                    setLoading((prev) => ({ ...prev, studyType: false }));
                });
        } catch (error) {
            console.error("Error fetching study types:", error);
            handleError("error", "Failed to fetch study types");
            setLoading((prev) => ({ ...prev, studyType: false }));
        }
    }, [productCategoryId, buildUrl, transformData, handleError]);

    const fetchStudyTypeMethods = useCallback(
        async (studyTypes) => {
            setLoading((prev) => ({ ...prev, studyTypeMethod: true }));
            try {
                const baseUrl = process.env.REACT_APP_SERVER_PATH + `/study-type-methods/all/`;
                const url = buildUrl(baseUrl, productCategoryId, { study_type: studyTypes.join(",") });

                await api
                    .get(url)
                    .then((res) => {
                        const data = Array.isArray(res.data) ? res.data : [];
                        const options = transformData(data, "study_type_method");
                        setStudyTypeMethodOptions(options);
                        setLoading((prev) => ({ ...prev, studyTypeMethod: false }));
                    })
                    .catch((error) => {
                        console.error("Error fetching study type methods:", error);
                        handleError("error", "Failed to fetch study type methods");
                        setLoading((prev) => ({ ...prev, studyTypeMethod: false }));
                    });
            } catch (error) {
                console.error("Error fetching study type methods:", error);
                handleError("error", "Failed to fetch study type methods");
                setLoading((prev) => ({ ...prev, studyTypeMethod: false }));
            }
        },
        [productCategoryId, buildUrl, transformData, handleError]
    );

    const fetchBackendVariables = useCallback(
        async (studyTypes, studyTypeMethods) => {
            setLoading((prev) => ({ ...prev, backendVariable: true }));
            try {
                const baseUrl = process.env.REACT_APP_SERVER_PATH + `/data-mappings/all/`;
                const url = buildUrl(baseUrl, productCategoryId, {
                    study_type: studyTypes.join(","),
                    study_type_method: studyTypeMethods.join(","),
                });

                await api
                    .get(url)
                    .then((res) => {
                        let data = Array.isArray(res.data) ? res.data.map((ele) => ele.backend_variable) : [];
                        data = Array.from(new Set(data));
                        const options = transformData(data, "backend_variable");
                        setBackendVariableOptions(options);
                        setLoading((prev) => ({ ...prev, backendVariable: false }));
                    })
                    .catch((error) => {
                        console.error("Error fetching backend variables:", error);
                        handleError("error", "Failed to fetch backend variables");
                        setLoading((prev) => ({ ...prev, backendVariable: false }));
                    });
            } catch (error) {
                console.error("Error fetching backend variables:", error);
                handleError("error", "Failed to fetch backend variables");
                setLoading((prev) => ({ ...prev, backendVariable: false }));
            }
        },
        [productCategoryId, buildUrl, transformData, handleError]
    );

    // Fetch Study Types when dialog opens
    useEffect(() => {
        if (open && productCategoryId) {
            fetchStudyTypes();
        }
    }, [open, productCategoryId, fetchStudyTypes]);

    // Fetch Study Type Methods when Study Type is selected
    useEffect(() => {
        if (selectedStudyType && selectedStudyType.length > 0) {
            fetchStudyTypeMethods(selectedStudyType.map((st) => st.value));
        } else {
            setStudyTypeMethodOptions([]);
            setSelectedStudyTypeMethod([]);
        }
    }, [selectedStudyType, fetchStudyTypeMethods]);

    // Fetch Backend Variables when Study Type Method is selected
    useEffect(() => {
        if (selectedStudyType && selectedStudyType.length > 0 && selectedStudyTypeMethod && selectedStudyTypeMethod.length > 0) {
            fetchBackendVariables(
                selectedStudyType.map((st) => st.value),
                selectedStudyTypeMethod.map((stm) => stm.value)
            );
        } else {
            setBackendVariableOptions([]);
            setSelectedBackendVariable([]);
        }
    }, [selectedStudyType, selectedStudyTypeMethod, fetchBackendVariables]);

    const handleStudyTypeChange = (selectedOptions) => {
        setSelectedStudyType(selectedOptions || []);
    };

    const handleStudyTypeMethodChange = (selectedOptions) => {
        setSelectedStudyTypeMethod(selectedOptions || []);
    };

    const handleBackendVariableChange = (selectedOptions) => {
        setSelectedBackendVariable(selectedOptions || []);
    };

    const handleDownloadDialogClose = () => {
        // Reset all dropdown states when closing
        setSelectedStudyType([]);
        setSelectedStudyTypeMethod([]);
        setSelectedBackendVariable([]);
        setStudyTypeMethodOptions([]);
        setBackendVariableOptions([]);
        setOpen(false);
    };

    const handleAuditingVariablesExport = () => {
        if (!selectedStudyType || selectedStudyType.length === 0 || !selectedStudyTypeMethod || selectedStudyTypeMethod.length === 0) {
            handleError("warning", "Please select all fields: Study Type and Study Type Method");
            return;
        }

        // Extract values from selected
        const productCategoryIdValues = productCategoryId.toString();
        const studyTypeValues = selectedStudyType.map((st) => st.value).join(",");
        const studyTypeMethodValues = selectedStudyTypeMethod.map((stm) => stm.value).join(",");
        const backendVariableValues = selectedBackendVariable.map((bv) => bv.value).join(",");

        const payload = {
            product_category: productCategoryIdValues,
            study_type: studyTypeValues,
            study_type_method: studyTypeMethodValues,
            backend_variable: backendVariableValues,
        };

        setIsLoading(true);
        try {
            const url = process.env.REACT_APP_SERVER_PATH + `/data-mappings/export-view/`;
            api.post(url, { ...payload }, { responseType: "arraybuffer" })
                .then((res) => {
                    const blob = new Blob([res.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });

                    const disposition = res.headers["content-disposition"] || res.headers["Content-Disposition"]; // to get file name

                    let filename = "auditing-variables-document.xlsx"; // Default filename

                    if (disposition && disposition.indexOf("attachment") !== -1) {
                        const matches = /filename[^;=\n]*=((['"]).*?(\2|)|[^;\n]*)/.exec(disposition);
                        if (matches != null && matches[1]) {
                            filename = matches[1].replace(/['"]/g, ""); // Remove quotes from the filename
                        }
                    }
                    // Create an anchor element and trigger a click event to initiate the download
                    const link = document.createElement("a");
                    link.href = window.URL.createObjectURL(blob);
                    link.download = filename;
                    link.click();
                    setIsLoading(false);
                    handleDownloadDialogClose();
                })
                .catch((error) => {
                    handleError("error", error);
                    setIsLoading(false);
                });
        } catch (error) {
            handleError("error", error);
            setIsLoading(false);
        }
    };

    const actions = () => {
        return (
            <div
                style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    gap: "16px",
                    width: "100%",
                }}
            >
                <Button
                    onClick={handleDownloadDialogClose}
                    variant="outlined"
                    color="primary"
                    className="custom-btn custom-btn-outlined color-blue"
                >
                    Cancel
                </Button>
                <Button
                    onClick={handleAuditingVariablesExport}
                    variant="contained"
                    color="primary"
                    className="custom-btn liner-light-blue-background"
                    style={{
                        backgroundColor: "#005eef",
                    }}
                >
                    Export to Excel
                </Button>
            </div>
        );
    };

    const content = () => {
        return (
            <div
                style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "16px",
                    position: "relative",
                }}
            >
                <div style={{ display: "flex", alignItems: "center" }}>
                    <div className="col-4">
                        <label
                            htmlFor="study-type-select"
                            className="input-label text-sm "
                        >
                            Study Type :
                        </label>
                    </div>
                    <div style={{ width: "70%", position: "relative" }}>
                        <Select
                            id="study-type-select"
                            styles={{
                                ...ReactSelectStyles,
                                menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                                menu: (base) => ({ ...base, zIndex: 9999 }),
                                control: (base) => ({
                                    ...base,
                                    minHeight: "30px",
                                    height: "auto",
                                    alignItems: "center",
                                }),
                            }}
                            className="dropdown dropdown-first-row"
                            onChange={handleStudyTypeChange}
                            value={selectedStudyType}
                            placeholder={
                                <span>
                                    Select <i>Study Type</i>
                                </span>
                            }
                            options={studyTypeOptions}
                            isDisabled={loading.studyType}
                            menuPortalTarget={document.body}
                            menuPosition="fixed"
                            isMulti
                            closeMenuOnSelect={false}
                            hideSelectedOptions={false}
                            components={{
                                Option: CustomOption,
                            }}
                        />
                        {loading.studyType && (
                            <CircularProgress
                                size={12}
                                style={{ position: "absolute", right: "10px", top: "50%", transform: "translateY(-50%)" }}
                            />
                        )}
                    </div>
                </div>

                <div style={{ display: "flex", alignItems: "center" }}>
                    <div className="col-4">
                        <label
                            htmlFor="study-type-method-select"
                            className="input-label text-sm "
                        >
                            Study Type Method :
                        </label>
                    </div>
                    <div style={{ width: "70%", position: "relative" }}>
                        <Select
                            id="study-type-method-select"
                            styles={{
                                ...ReactSelectStyles,
                                menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                                menu: (base) => ({ ...base, zIndex: 9999 }),
                                control: (base) => ({
                                    ...base,
                                    minHeight: "30px",
                                    height: "auto",
                                    alignItems: "center",
                                }),
                            }}
                            className="dropdown dropdown-first-row"
                            onChange={handleStudyTypeMethodChange}
                            value={selectedStudyTypeMethod}
                            placeholder={
                                selectedStudyType && selectedStudyType.length > 0 ? (
                                    <span>
                                        Select <i>Study Type Method</i>
                                    </span>
                                ) : (
                                    <span>
                                        Select <i>Study Type</i> to enable this option
                                    </span>
                                )
                            }
                            options={studyTypeMethodOptions}
                            isDisabled={!selectedStudyType || selectedStudyType.length === 0 || loading.studyTypeMethod}
                            menuPortalTarget={document.body}
                            menuPosition="fixed"
                            isMulti
                            closeMenuOnSelect={false}
                            hideSelectedOptions={false}
                            components={{
                                Option: CustomOption,
                            }}
                        />
                        {loading.studyTypeMethod && (
                            <CircularProgress
                                size={12}
                                style={{ position: "absolute", right: "10px", top: "50%", transform: "translateY(-50%)" }}
                            />
                        )}
                    </div>
                </div>

                <div style={{ display: "flex", alignItems: "center" }}>
                    <div className="col-4">
                        <label
                            htmlFor="backend-variable-select"
                            className="input-label text-sm "
                        >
                            Backend Variable :
                        </label>
                    </div>
                    <div style={{ width: "70%", position: "relative" }}>
                        <Select
                            id="backend-variable-select"
                            styles={{
                                ...ReactSelectStyles,
                                menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                                menu: (base) => ({ ...base, zIndex: 9999 }),
                                control: (base) => ({
                                    ...base,
                                    minHeight: "30px",
                                    height: "auto",
                                    alignItems: "center",
                                }),
                            }}
                            className="dropdown dropdown-first-row"
                            onChange={handleBackendVariableChange}
                            value={selectedBackendVariable}
                            placeholder={
                                selectedStudyTypeMethod && selectedStudyTypeMethod.length > 0 ? (
                                    <span>
                                        Select <i>Backend Variable</i>
                                    </span>
                                ) : (
                                    <span>
                                        Select <i>Study Type Method</i> to enable this option
                                    </span>
                                )
                            }
                            options={backendVariableOptions}
                            isDisabled={!selectedStudyTypeMethod || selectedStudyTypeMethod.length === 0 || loading.backendVariable}
                            menuPortalTarget={document.body}
                            menuPosition="fixed"
                            isMulti
                            closeMenuOnSelect={false}
                            hideSelectedOptions={false}
                            components={{
                                Option: CustomOption,
                            }}
                        />
                        {loading.backendVariable && (
                            <CircularProgress
                                size={12}
                                style={{ position: "absolute", right: "10px", top: "50%", transform: "translateY(-50%)" }}
                            />
                        )}
                    </div>
                </div>
                <div
                    style={{
                        color: "#000000",
                        fontSize: "10px",
                        fontWeight: "600",
                        margin: "10px 0 0 0",
                        textAlign: "left",
                        paddingBottom: "10px",
                        paddingTop: "5px",
                    }}
                >
                    <span>
                        Note : When a <i>Study Type</i> and its corresponding <i>Study Type Method</i> are selected, Super Users should be able to download data mapping records for all backend variables linked to that selection.
                    </span>
                </div>
            </div>
        );
    };

    return (
        <>
            {isLoading && <Loader />}
            <Dialog
                open={open}
                maxWidth={"sm"}
                fullWidth
            >
                <DialogTitle
                    style={{
                        backgroundColor: "rgb(51, 94, 234,0.1)",
                        color: "rgb(51, 94, 234)",
                        fontSize: 16,
                        textAlign: "center",
                        padding: "10px 12px",
                    }}
                >
                    Export Data Mapping View
                    <ClearIcon
                        sx={{
                            position: "absolute",
                            right: 8,
                            top: 10,
                            color: "rgb(51, 94, 234) !important",
                            cursor: "pointer",
                        }}
                        size="small"
                        onClick={handleDownloadDialogClose}
                    />
                </DialogTitle>
                <DialogContent
                    style={{
                        padding: "20px 40px",
                    }}
                >
                    {content()}
                </DialogContent>
                <DialogActions>{actions()}</DialogActions>
            </Dialog>
        </>
    );
}

// Add PropTypes validation
AuditingVariablesDocumentDialog.propTypes = {
    open: PropTypes.bool,
    setOpen: PropTypes.func.isRequired,
};

export default AuditingVariablesDocumentDialog;
